import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'

import {
  beginDragMeasurement,
  readWidthPx,
  setWidthPx,
  LEFT_DRAWER_MIN_WIDTH_PX,
  LEFT_DRAWER_MAX_WIDTH_PX,
} from '@app/application/behaviors/drawers/left-drawer-layout'

// Below this a pointer gesture counts as a click, not a drag, so a stray wobble
// can never nudge the width.
const DRAG_THRESHOLD_PX = 4
const KEYBOARD_STEP_PX = 16
// Page-wide drag mode: keeps the resize cursor, suppresses text selection, and
// drops the layout's width transitions (see two-drawer-layout-new.scss) so the
// drawer tracks the pointer 1:1.
const RESIZING_CLASS = 'left-drawer-resizing'
// A keyboard step is instantaneous and has no release event, so its
// drawer:animation-end waits until the steps stop arriving. Long enough to
// absorb key auto-repeat, short enough that consumers settle promptly.
const KEYBOARD_MOTION_IDLE_MS = 300

// Drag-to-resize handle on the right edge of the new layout's left drawer.
// Stimulus rather than Vue: the drawer and the handle are server-rendered slim,
// and the only state is this element's own gesture.
export default class extends Controller<HTMLElement> {
  private dragging = false
  private dragMoved = false
  private startX = 0
  // Drawer width in pixels, mirrored to aria-valuenow: seeded from the rendered
  // layout, then tracked from the clamped value `setWidthPx` returns.
  private width: number | null = null
  // Geometry snapshot for the active drag, taken on pointerdown; null when the
  // drawer element is missing.
  private dragMeasure: ((clientX: number) => number) | null = null
  // The pointer that started the drag. Capture retargets only that pointer, and
  // these listeners sit on window, so without this a second touch point would
  // steer the resize or end it at its own position.
  private dragPointerId: number | undefined

  connect(): void {
    this.element.setAttribute('aria-valuemin', String(LEFT_DRAWER_MIN_WIDTH_PX))
    this.element.setAttribute('aria-valuemax', String(LEFT_DRAWER_MAX_WIDTH_PX))
    this.syncWidth()

    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerCancel)
  }

  disconnect(): void {
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerCancel)

    this.setDragging(false)
    this.closeKeyboardMotion.flush()
  }

  startDrag(event: PointerEvent): void {
    if (event.button !== 0) return
    event.preventDefault()

    this.startX = event.clientX
    this.dragMoved = false
    this.dragMeasure = beginDragMeasurement()
    this.dragPointerId = event.pointerId
    this.setDragging(true)

    // Capture guarantees pointerup/pointercancel delivery even when released
    // outside the window. The guard narrows the type before the call.
    if (event.pointerId !== undefined) {
      this.element.setPointerCapture(event.pointerId)
    }
  }

  // ARIA window-splitter keyboard pattern: arrows step the width, Home/End jump
  // to the bounds. Each step persists, because there is no drop to persist on.
  nudge(event: KeyboardEvent): void {
    if (this.width === null) return

    let target: number
    switch (event.key) {
      case 'ArrowLeft':
        target = this.width - KEYBOARD_STEP_PX
        break
      case 'ArrowRight':
        target = this.width + KEYBOARD_STEP_PX
        break
      case 'Home':
        target = LEFT_DRAWER_MIN_WIDTH_PX
        break
      case 'End':
        target = LEFT_DRAWER_MAX_WIDTH_PX
        break
      default:
        return
    }

    event.preventDefault()
    this.applyWidth(target, { persist: true })
    this.announceKeyboardMotion()
  }

  // The rendered width can have moved since connect (a viewport resize crossing
  // a breakpoint), so re-read it as the baseline for the arrow keys.
  onFocus(): void {
    this.syncWidth()
  }

  private isDragPointer(event: PointerEvent): boolean {
    return this.dragging && event.pointerId === this.dragPointerId
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return
    if (Math.abs(event.clientX - this.startX) > DRAG_THRESHOLD_PX) {
      this.dragMoved = true
    }
    if (!this.dragMoved || !this.dragMeasure) return

    this.applyWidth(this.dragMeasure(event.clientX), { persist: false })
  }

  private onPointerUp = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return

    // Land the final width while the transition is still suppressed. Lifting
    // the suppression first eases the last frame of travel, because pointerup
    // carries the true release position while pointermove is coalesced to the
    // frame rate.
    if (this.dragMoved && this.dragMeasure) {
      this.applyWidth(this.dragMeasure(event.clientX), { persist: true })
    }

    this.setDragging(false)
  }

  // A cancelled stream (e.g. touch gesture takeover) abandons the drag: the
  // live width stays as dragged, but nothing is persisted.
  private onPointerCancel = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return
    this.setDragging(false)
  }

  // The handle's :focus-visible rule suppresses the transition, so a keyboard
  // step moves the drawer without firing transition events, exactly as a drag
  // does. Consumers that reposition on drawer motion need the pair either way.
  private keyboardMotionOpen = false

  private closeKeyboardMotion = debounce((): void => {
    if (!this.keyboardMotionOpen) return
    this.keyboardMotionOpen = false
    window.eventHub.$emit('drawer:animation-end')
  }, KEYBOARD_MOTION_IDLE_MS)

  private announceKeyboardMotion(): void {
    if (!this.keyboardMotionOpen) {
      this.keyboardMotionOpen = true
      window.eventHub.$emit('drawer:animation-start')
    }
    this.closeKeyboardMotion()
  }

  private applyWidth(px: number, options: { persist: boolean }): void {
    this.setWidth(setWidthPx(px, options))
  }

  private syncWidth(): void {
    const width = readWidthPx()
    if (width !== null) this.setWidth(width)
  }

  // Clamped because on a page that loads collapsed the rendered width
  // `syncWidth` reads is `$left-sidebar-collapsed-width`, which would announce
  // an aria-valuenow below the aria-valuemin set alongside it.
  private setWidth(px: number): void {
    this.width = Math.min(
      LEFT_DRAWER_MAX_WIDTH_PX,
      Math.max(LEFT_DRAWER_MIN_WIDTH_PX, px)
    )
    this.element.setAttribute('aria-valuenow', String(this.width))
  }

  private setDragging(dragging: boolean): void {
    if (this.dragging === dragging) return

    this.dragging = dragging
    // Drives the handle's active styling, which cannot lean on :hover once the
    // pointer has travelled off the handle.
    this.element.dataset.dragging = String(dragging)
    document.body.classList.toggle(RESIZING_CLASS, dragging)

    // A drag writes the width directly, so it fires no transition events.
    // Consumers that reposition on drawer motion need the pair announced here
    // or they hold coordinates taken before the drag.
    window.eventHub.$emit(
      dragging ? 'drawer:animation-start' : 'drawer:animation-end'
    )
  }
}
